import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../core/state';
import { AuthService } from '../services/auth.service';

import * as UserActions from '../../shared/core/state/users/user.actions';
import { isBackendApiRequest } from './helper.utils';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private store = inject<Store<State>>(Store);
  private authService = inject(AuthService);

  private isRefreshing = false;
  // Subject to broadcast the new access token to queued requests
  private accessTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!isBackendApiRequest(req.url)) {
      return next.handle(req);
    }

    return new Observable((observer) => {
      this.authService.getToken().then((authToken) => {
        if (authToken) {
          const authReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + authToken),
          });

          // Let 401 handler deal with expired tokens, don't pre-logout
          next
            .handle(authReq)
            .pipe(
              catchError((error: HttpErrorResponse) => {
                if (
                  error.status === 401 &&
                  !req.url.includes('/auth/refresh')
                ) {
                  return this.handle401Error(authReq, next);
                }
                return throwError(() => error);
              }),
            )
            .subscribe(observer);
        } else {
          next.handle(req).subscribe(observer);
        }
      });
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.accessTokenSubject.next(null);

      return from(this.authService.refreshToken()).pipe(
        switchMap((newToken) => {
          if (!newToken) throw new Error('Authentication expired. Please log in again.');
          this.accessTokenSubject.next(newToken);
          const authReq = request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + newToken),
          });
          return next.handle(authReq);
        }),
        catchError((err) => {
          this.accessTokenSubject.error(err);
          this.accessTokenSubject = new BehaviorSubject<string | null>(null);
          this.store.dispatch(UserActions.logout({ redirect: true }));
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      );
    } else {
      // Wait for token refresh to complete
      return this.accessTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          const authReq = request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + token),
          });
          return next.handle(authReq);
        }),
      );
    }
  }
}
